import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FiX, FiEye, FiEyeOff } from 'react-icons/fi';
import { getFormErrorMessage } from '../../utils/helpers';
import LoadingSpinner from '../common/LoadingSpinner';
import { USER_ROLES } from '../../utils/constants';
import { Modal } from '../../design-system';

const UserForm = ({ user, availableRoles, availableEstablishments, currentUser, onSave, onClose }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!user;
  const isEstablishmentAdmin = currentUser?.role === USER_ROLES.ESTABLISHMENT_ADMIN || currentUser?.role === USER_ROLES.MANAGER;
  const isSalesOrPlatformAdmin = currentUser?.role === USER_ROLES.PLATFORM_ADMIN || currentUser?.role === USER_ROLES.SALES;
  const shouldShowEstablishmentField = isSalesOrPlatformAdmin;
  const isEditingOwnUser = isEditing && user?.id === currentUser?.id;

  // Helper function to convert UTC date to local datetime-local format
  const formatDateForInput = (utcDateString) => {
    if (!utcDateString) return '';

    const date = new Date(utcDateString);
    // Get the local date and time components
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      name: user?.name || '',
      username: user?.username || '',
      email: user?.email || '',
      role: user?.role || availableRoles[0]?.value || '',
      establishment_id: user?.establishment_id || '',
      hourly_rate: user?.hourly_rate || '',
      documents_url: user?.documents_url || '',
      is_active: user?.is_active ?? true,
      expiration_date: formatDateForInput(user?.expiration_date),
      password: '',
      confirmPassword: '',
    },
  });

  const watchPassword = watch('password');
  const watchRole = watch('role');

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      const userData = {
        name: data.name,
        username: data.username,
        email: data.email || null,
        role: data.role,
        establishment_id: isEstablishmentAdmin ? currentUser.establishment_id : (isSalesOrPlatformAdmin ? (data.establishment_id || null) : currentUser.establishment_id),
        hourly_rate: data.hourly_rate ? parseFloat(data.hourly_rate) : null,
        documents_url: data.documents_url || null,
        is_active: data.is_active,
        // Don't allow setting expiration date on own user
        expiration_date: isEditingOwnUser ? (user.expiration_date || null) : (data.expiration_date ? new Date(data.expiration_date).toISOString() : null),
      };

      // Only include password if provided
      if (data.password) {
        userData.password = data.password;
      }

      await onSave(userData);
    } catch (error) {
      console.error('Error saving user:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      size="lg"
      showCloseButton={false}
    >
      <Modal.Header>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            {isEditing ? 'Editar Usuario' : 'Nuevo Usuario'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>
      </Modal.Header>

      <Modal.Body className="max-h-[60vh] overflow-y-auto">
        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" id="user-form">
              {/* Name */}
              <div>
                <label htmlFor="name" className="form-label">
                  Nombre completo *
                </label>
                <input
                  id="name"
                  type="text"
                  className={`form-input ${errors.name ? 'border-danger-500' : ''}`}
                  placeholder="Nombre completo del usuario"
                  {...register('name', {
                    required: true,
                    minLength: 2,
                  })}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-danger-600">
                    {getFormErrorMessage(errors, 'name')}
                  </p>
                )}
              </div>

              {/* Username */}
              <div>
                <label htmlFor="username" className="form-label">
                  Nombre de usuario *
                </label>
                <input
                  id="username"
                  type="text"
                  className={`form-input ${errors.username ? 'border-danger-500' : ''}`}
                  placeholder="Nombre de usuario único"
                  {...register('username', {
                    required: true,
                    minLength: 3,
                    pattern: /^[a-zA-Z0-9_]+$/,
                  })}
                />
                {errors.username && (
                  <p className="mt-1 text-sm text-danger-600">
                    {errors.username.type === 'pattern'
                      ? 'Solo letras, números y guiones bajos'
                      : getFormErrorMessage(errors, 'username')
                    }
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="form-label">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  className={`form-input ${errors.email ? 'border-danger-500' : ''}`}
                  placeholder="email@example.com"
                  {...register('email', {
                    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  })}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-danger-600">
                    Formato de email inválido
                  </p>
                )}
              </div>

              {/* Documents URL */}
              <div>
                <label htmlFor="documents_url" className="form-label">
                  URL de carpeta de documentos
                </label>
                <input
                  id="documents_url"
                  type="url"
                  className={`form-input ${errors.documents_url ? 'border-danger-500' : ''}`}
                  placeholder="https://drive.google.com/... o https://onedrive.live.com/..."
                  {...register('documents_url', {
                    pattern: /^https?:\/\/.+/,
                  })}
                />
                <p className="mt-1 text-xs text-gray-500">
                  URL a la carpeta de OneDrive, Google Drive u otro servicio donde se almacena la información del empleado
                </p>
                {errors.documents_url && (
                  <p className="mt-1 text-sm text-danger-600">
                    Formato de URL inválido (debe comenzar con http:// o https://)
                  </p>
                )}
              </div>

              {/* Role */}
              <div>
                <label htmlFor="role" className="form-label">
                  Rol *
                </label>
                <select
                  id="role"
                  className={`form-input ${errors.role ? 'border-danger-500' : ''}`}
                  {...register('role', { required: true })}
                >
                  {availableRoles.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
                {errors.role && (
                  <p className="mt-1 text-sm text-danger-600">
                    {getFormErrorMessage(errors, 'role')}
                  </p>
                )}
              </div>

              {/* Establishment - Only show for platform admins */}
              {shouldShowEstablishmentField && (
                <div>
                  <label htmlFor="establishment_id" className="form-label">
                    Establecimiento
                  </label>
                  <select
                    id="establishment_id"
                    className={`form-input ${errors.establishment_id ? 'border-danger-500' : ''}`}
                    {...register('establishment_id')}
                  >
                    <option value="">Seleccionar establecimiento (opcional)</option>
                    {availableEstablishments?.map(establishment => (
                      <option key={establishment.id} value={establishment.id}>
                        {establishment.name}
                      </option>
                    ))}
                  </select>
                  {errors.establishment_id && (
                    <p className="mt-1 text-sm text-danger-600">
                      {getFormErrorMessage(errors, 'establishment_id')}
                    </p>
                  )}
                </div>
              )}

              {/* Hourly Rate - Only show for cleaning staff */}
              {watchRole === USER_ROLES.CLEANING_STAFF && (
                <div>
                  <label htmlFor="hourly_rate" className="form-label">
                    Coste por hora (€)
                  </label>
                  <input
                    id="hourly_rate"
                    type="number"
                    step="0.01"
                    min="0"
                    className={`form-input ${errors.hourly_rate ? 'border-danger-500' : ''}`}
                    placeholder="0.00"
                    {...register('hourly_rate', {
                      min: 0,
                      valueAsNumber: false,
                    })}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Coste por hora del personal para calcular gastos en reportes
                  </p>
                  {errors.hourly_rate && (
                    <p className="mt-1 text-sm text-danger-600">
                      El coste por hora debe ser un valor positivo
                    </p>
                  )}
                </div>
              )}

              {/* Password - Only for new users */}
              {!isEditing && (
                <>
                  <div>
                    <label htmlFor="password" className="form-label">
                      Contraseña *
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        className={`form-input pr-10 ${errors.password ? 'border-danger-500' : ''}`}
                        placeholder="Contraseña"
                        {...register('password', {
                          required: true,
                          minLength: 8,
                        })}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <FiEyeOff className="h-5 w-5 text-gray-400" />
                        ) : (
                          <FiEye className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="mt-1 text-sm text-danger-600">
                        {errors.password.type === 'minLength'
                          ? 'La contraseña debe tener al menos 8 caracteres'
                          : getFormErrorMessage(errors, 'password')
                        }
                      </p>
                    )}
                  </div>

                  {/* Confirm Password */}
                  {watchPassword && (
                    <div>
                      <label htmlFor="confirmPassword" className="form-label">
                        Confirmar contraseña *
                      </label>
                      <input
                        id="confirmPassword"
                        type="password"
                        className={`form-input ${errors.confirmPassword ? 'border-danger-500' : ''}`}
                        placeholder="Confirmar contraseña"
                        {...register('confirmPassword', {
                          required: !!watchPassword,
                          validate: value => value === watchPassword || 'Las contraseñas no coinciden',
                        })}
                      />
                      {errors.confirmPassword && (
                        <p className="mt-1 text-sm text-danger-600">
                          {errors.confirmPassword.message}
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Expiration date */}
              <div>
                <label htmlFor="expiration_date" className="form-label">
                  Fecha de caducidad (opcional)
                </label>
                <input
                  id="expiration_date"
                  type="datetime-local"
                  className={`form-input ${errors.expiration_date ? 'border-danger-500' : ''} ${isEditingOwnUser ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  disabled={isEditingOwnUser}
                  {...register('expiration_date')}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {isEditingOwnUser
                    ? 'No puedes establecer fecha de caducidad a tu propia cuenta'
                    : 'Si se establece, el usuario no podrá iniciar sesión después de esta fecha'
                  }
                </p>
                {errors.expiration_date && (
                  <p className="mt-1 text-sm text-danger-600">
                    {getFormErrorMessage(errors, 'expiration_date')}
                  </p>
                )}
              </div>

              {/* Active status */}
              <div className="flex items-center">
                <input
                  id="is_active"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  {...register('is_active')}
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                  Usuario activo
                </label>
              </div>

        </form>
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          onClick={onClose}
          className="btn btn-secondary"
          disabled={isSubmitting}
        >
          Cancelar
        </button>
        <button
          type="submit"
          form="user-form"
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <div className="flex items-center space-x-2">
              <LoadingSpinner size="sm" color="white" />
              <span>Guardando...</span>
            </div>
          ) : (
            isEditing ? 'Actualizar' : 'Crear'
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default UserForm;